import { computed } from '@preact/signals-react';
import { AppointmentStatus } from 'Turn/services/constants';
import { Tabs, TabsProps } from 'antd';
import ButtonConfirm from 'components/ButtonConfirm';
import { loadingSignal } from 'components/Loading';
import get from 'lodash/get';
import set from 'lodash/set';
import sumBy from 'lodash/sumBy';
import { memo, useMemo, useState } from 'react';
import aptActions from 'services/actions/apt';
import shopSelectors from 'services/selectors/shop';
import { GROUP_NOTE_SEPARATOR } from 'Calendar/appointment-style-a';
import { UNDO_CHECK_IN_CONFIRM } from 'AppointmentList/status-transitions';
import { IPayloadBookingV2, IPayloadBookingV2Item } from 'services/types/request';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import storage from 'utils/sessionStorage';
import ModalNumberPad, { useModalNumberPadRef } from 'widgets/ModalNumberPad';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import CustomerTab from './CustomerTab';
import Modal from './Modal';
import ModalConfirmGroup, { IGroupSource, IStaffAssignItem, useModalConfirmGroupRef } from './ModalConfirmGroup';
import ModalReason from './ModalReason';
import ModalConfirmScope from './ModalConfirmScope';
import ReviewTab from './ReviewTab';
import ServiceTab from './ServiceTab';
import StaffTab from './StaffTab';
import TimeTab from './TimeTab';
import { ADD_APPOINTMENT_TABS, ANYONE_NAILS_ID } from './helper';
import aptSignal, { totalDurationSignalDefault } from './signal';
import { confirmSameDayBooking } from 'widgets/SameDayBookingAlert/confirm-same-day-booking';
import { resolveBookingDuration } from './booking-duration';

const NewAppointment = () => {
  const modalNumberPadRef = useModalNumberPadRef();
  const modalConfirmGroupRef = useModalConfirmGroupRef();
  const dispatch = useAppDispatch();
  // Style A shop. Drives two things here: the group note always carries its `Group:` prefix (the
  // calendar reads that prefix to color the card), and the detail footer offers CHECK-IN with no
  // preceding CONFIRM -- passing `confirmed` takes the footer's existing CHECK-IN branch, and the
  // backend allows NEW -> CHECK_IN directly. Deliberately not view-gated: the modal opens from every
  // view and the confirm flow should not depend on which one the user opened it from.
  const styleA = shopSelectors.setting().appointmentStyleA;
  const allowUndoCheckIn = shopSelectors.setting().allowUndoCheckIn;
  // PIN da nhap, giu lai cho toi luc bam dong y. null = chua co yeu cau go check-in nao.
  const [revertPinCode, setRevertPinCode] = useState<string | null>(null);
  const [visibleReason, setVisibleReason] = useState<string>('');
  // Giu lai thao tac dang cho chon pham vi. null = khong co modal pham vi nao dang mo.
  const [pendingScope, setPendingScope] = useState<
    { action: 'delete' | 'cancel'; pinCode: string; reason?: string } | null
  >(null);
  const activeKey = computed<string>(() => aptSignal.tabSignal.value).value;

  const isValid = computed(() => {
    const { customerSignal, staffIdSignal, timeSignal, servicesSignal, tabSignal } = aptSignal;

    // Doc rieng tung signal truoc khi ghep, khong xau chuoi bang `&&`. Chuoi `&&` dung
    // sau signal dau tien cho ket qua falsy, nen so signal duoc doc thay doi theo du lieu:
    // modal dong (signal da bi xoa) doc 1 cai, mo lai voi day du du lieu doc 5 cai.
    // @preact/signals-react v1 gan hook theo tung signal doc duoc luc render, nen so hook
    // cua component nay se lech giua hai lan render, va React tuot slot sang hook ke tiep.
    const hasCustomer = !!customerSignal.value?.id;
    const hasStaff = !!staffIdSignal.value;
    const hasTime = !!timeSignal.value;
    const hasServices = !!servicesSignal.value?.length;
    const onReviewTab = tabSignal.value === ADD_APPOINTMENT_TABS.REVIEW;

    return hasCustomer && hasStaff && hasTime && hasServices && onReviewTab;
  }).value;

  const staffLocked = aptSignal.isStaffLocked();

  const items: TabsProps['items'] = useMemo(() => {
    const CUSTOMER = { key: ADD_APPOINTMENT_TABS.CUSTOMER, label: <CustomerTab.Label />, children: <CustomerTab.Children /> };
    const STAFF = { key: ADD_APPOINTMENT_TABS.STAFF, label: <StaffTab.Label />, children: <StaffTab.Children /> };
    const TIME = { key: ADD_APPOINTMENT_TABS.TIME, label: <TimeTab.Label />, children: <TimeTab.Children /> };
    const SERVICES = { key: ADD_APPOINTMENT_TABS.SERVICE, label: <ServiceTab.Label />, children: <ServiceTab.Children /> };
    const REVIEW = { key: ADD_APPOINTMENT_TABS.REVIEW, label: <ReviewTab.Label />, children: <ReviewTab.Children /> };
    if (aptSignal.detailIdSignal.value) {
      // Lich da khoa tho thi bo han tab STAFF, khong chi an nut: tab van bam duoc tu thanh tab.
      return staffLocked ? [TIME, SERVICES, REVIEW] : [STAFF, TIME, SERVICES, REVIEW];
    }
    return ([CUSTOMER, STAFF, TIME, SERVICES, REVIEW]);
  }, [aptSignal.detailIdSignal.value, staffLocked]);

  const getMappingPayload = () => {
    const values = aptSignal.getValues();

    const staffId = values.staffId?.toString();

    const objectStaff: Record<string, { serviceId: number, duration: number; }[]> = {};
    values.services.map(o => {
      const item = {
        serviceId: +o.serviceId,
        duration: o.duration,
      };
      if (get(objectStaff, o.staffId)) {
        objectStaff[o.staffId].push(item);
      } else {
        set(objectStaff, [o.staffId], [item]);
      }
    });

    const staffs: { staffId: number, serviceIds: number[], totalDuration: number }[] = Object.entries(objectStaff).map(([staffId, services]) => ({
      staffId: +staffId,
      serviceIds: services.map(o => o.serviceId),
      totalDuration: sumBy(services, o => o.duration) || 0,
    }));

    const startTimeTemp = values.time?.clone();
    const items: IPayloadBookingV2Item[] = staffs.map(o => {
      const duration = resolveBookingDuration({
        isUpdate: !!aptSignal.detailIdSignal.value,
        serviceIdsBefore: aptSignal.detailServiceIdsSignal.value,
        serviceIdsNow: values.services.map(s => s.serviceId),
        staffGroupCount: staffs.length,
        servicesDuration: o.totalDuration,
        originalDuration: totalDurationSignalDefault.value,
      });
      return ({
        staffId: parseInt(aptSignal.staffIdMasterSignal.value?.toString() || o.staffId.toString()) || 0,
        serviceIds: o.serviceIds,
        startTime: startTimeTemp?.format('MM/DD/YYYY HH:mm') || '',
        endTime: startTimeTemp?.add(duration, 'minute').format('MM/DD/YYYY HH:mm') || '',
        // The server reads each item's own flag before the booking-wide one, and an item
        // sent without it is saved as not requested — so every item must carry it.
        requestStaff: values.requestStaff,
      });
    });
    const customerId = values.customer?.id || '';
    const data: IPayloadBookingV2 = {
      customerId: (!customerId || customerId === '0') ? 0 : +customerId,
      customerName: values.customer?.name || '',
      bookingType: 0,
      items,
      totalPeople: values.totalPeople,
      note: values.note || '',
      isAnybody: staffId === ANYONE_NAILS_ID ? 1 : 0,
      apiKey: storage.shopId.get(),
      requestStaff: values.requestStaff,
    };

    if (!customerId || customerId === '0') {
      delete data.customerName;
    }

    return data;
  };

  const handleDone = async () => {
    const data = getMappingPayload();
    // Khach da co lich trong ngay → canh bao + PIN staff. PIN do duoc gui kem de biet ai dat.
    const sameDay = await confirmSameDayBooking({
      customerId: data.customerId,
      startTime: data.items[0]?.startTime || '',
      totalPeople: data.totalPeople,
    });
    if (sameDay.status === 'cancel') return;
    dispatch(aptActions.bookAptV2(sameDay.actor ? { ...data, ...sameDay.actor } : data));
  };

  /**
   * State cua man dat lich thuong, dich sang shape modal gan tho doc duoc. Modal do khong
   * doc signal nao, vi Quick Booking cung dung no voi mot nguon state hoan toan khac.
   */
  const groupSourceFromAptSignal = (): IGroupSource => {
    const values = aptSignal.getValues();
    return {
      customer: values.customer,
      time: values.time,
      durationMinutes: values.totalDuration,
      serviceNames: values.services.map(o => o.name),
      preferredStaffId: values.staffId,
      defaultRequestStaff: values.requestStaff,
    };
  };

  const onBookingGroup = () => {
    const values = aptSignal.getValues();
    const startTime = values.time?.format('MM-DD-YYYY HH:mm') || '';
    const endTime = values.time?.clone().add(values.totalDuration, 'minute')?.format('MM-DD-YYYY HH:mm') || '';
    dispatch(aptActions.staffAvailableBooking.fetch({ startTime, endTime }));
    modalNumberPadRef.current?.open(totalPeople => {
      aptSignal.openSignal.value = false;
      modalConfirmGroupRef.current?.open(totalPeople, groupSourceFromAptSignal());
    });
  };

  const onGroupDone = (staffAssigns: IStaffAssignItem[]) => {
    const values = aptSignal.getValues();

    const startTime = values.time?.format('MM/DD/YYYY HH:mm') || '';
    const endTime = values.time?.clone().add(values.totalDuration, 'minute')?.format('MM/DD/YYYY HH:mm') || '';
    const staffId = values.staffId?.toString();
    const items: IPayloadBookingV2Item[] = staffAssigns.map(o => ({
      staffId: +o.staff.id,
      serviceIds: values.services.map(o => +o.serviceId),
      startTime,
      endTime,
      requestStaff: !!o.requestStaff,
    }));
    // The calendar identifies a group booking by the `Group:` prefix on its note, so under style A
    // the prefix is always written and any note the user typed is kept after it. Elsewhere the
    // prefix stays a fallback, leaving the note text of every other shop untouched.
    const groupPrefix = `Group: ${staffAssigns.length || 0} ${values.services.map(o => o.name).join(', ')}`;
    const note = styleA
      ? (values.note ? `${groupPrefix}${GROUP_NOTE_SEPARATOR}${values.note}` : groupPrefix)
      : (values.note || groupPrefix);
    const data: IPayloadBookingV2 = {
      customerId: +(values.customer?.id || ''),
      customerName: values.customer?.name || '',
      bookingType: 0,
      items,
      totalPeople: values.totalPeople,
      note: note,
      isAnybody: staffId === ANYONE_NAILS_ID ? 1 : 0,
      apiKey: storage.shopId.get(),
      // Co cap booking giu lai cho tuong thich: bat khi co bat ky lich nao trong nhom duoc request.
      requestStaff: staffAssigns.some(o => o.requestStaff),
    };
    dispatch(aptActions.bookAptV2(data));
  };

  const onModalConfirmGroupCancel = () => {
    aptSignal.openSignal.value = true;
  };

  const handleClose = () => {
    aptSignal.close();
  };

  const handleDelete = () => {
    verifyPinCode.current?.checkCancelPin((pinCode) => {
      if (!aptSignal.detailIdSignal.value) return;
      // Lich thuoc chuoi thi phai hoi pham vi truoc. Lich thuong giu nguyen luong cu,
      // khong them mot buoc bam nao.
      if (aptSignal.detailSeriesIdSignal.value) {
        setPendingScope({ action: 'delete', pinCode: pinCode || '' });
        return;
      }
      dispatch(aptActions.deleteApt({ aptId: aptSignal.detailIdSignal.value, pinCode: pinCode || '' }));
    });
  };

  /**
   * Repeat lich dang mo.
   *
   * Thu tu: PIN -> chon chu ky -> goi API, giong cac thao tac ghi khac. Nut bi khoa ngay
   * khi bam de hai request khong cung bay di; server cung co chot chan rieng.
   */
  const handleRepeat = (dayRepeat: number) => {
    verifyPinCode.current?.checkStaffPin((pinCode) => {
      if (!aptSignal.detailIdSignal.value) return;
      dispatch(aptActions.repeatApt({
        appointmentId: aptSignal.detailIdSignal.value,
        dayRepeat,
        pinCode: pinCode || '',
      }));
    });
  };

  const handleUpdate = () => {
    if (!aptSignal.detailIdSignal.value) return;
    const values = aptSignal.getValues();
    const data: IPayloadBookingV2 = getMappingPayload();
    set(data, 'id', +aptSignal.detailIdSignal.value);

    const isReUpdateWalkIn = values.customer?.id === 're-update-walk-in';
    if (isReUpdateWalkIn && values.customer?.phone) {
      set(data, 'phone', values.customer?.phone);
    }

    verifyPinCode.current?.checkStaffPin((pinCode) => {
      set(data, 'pinCode', pinCode);
      dispatch(aptActions.updateAptV2(data));
    });
  };

  const onCancelApt = () => {
    verifyPinCode.current?.checkCancelPin((pinCode) => {
      setVisibleReason(pinCode || '');
    });
  };

  const onConfirmApt = () => {
    verifyPinCode.current?.checkStaffPin((pinCode) => {
      if (!aptSignal.detailIdSignal.value) return;
      dispatch(aptActions.confirmApt({ appointmentId: aptSignal.detailIdSignal.value, pinCode: pinCode || '' }));
    });
  };

  /**
   * Go check-in ngay tren modal chi tiet. Cung mot thu tu voi cot status ben danh sach:
   * PIN -> xac nhan -> goi API, va cung mot cau xac nhan.
   */
  const onRevertCheckIn = () => {
    verifyPinCode.current?.forceVerifyAdmin((pinCode) => {
      if (!aptSignal.detailIdSignal.value) return;
      setRevertPinCode(pinCode || '');
    }, { title: 'PIN CODE', placeholder: 'Enter your Pin Code' });
  };

  const handleRevertCheckIn = () => {
    if (!aptSignal.detailIdSignal.value) return;
    dispatch(aptActions.changeAptStatus({
      appointmentId: aptSignal.detailIdSignal.value,
      status: AppointmentStatus.NEW,
      pinCode: revertPinCode || '',
    }));
    setRevertPinCode(null);
  };

  const onCheckInApt = () => {
    verifyPinCode.current?.checkAdmin(() => {
      if (!aptSignal.detailIdSignal.value) return;
      dispatch(aptActions.checkInApt(aptSignal.detailIdSignal.value));
    });
  };

  const handleCancelApt = (reason: string) => {
    if (!aptSignal.detailIdSignal.value) return;
    const pinCode = visibleReason || '';
    setVisibleReason('');
    // Thu tu bat buoc: PIN -> ly do -> pham vi. Chen modal pham vi som hon se lam mat ly do.
    if (aptSignal.detailSeriesIdSignal.value) {
      setPendingScope({ action: 'cancel', pinCode, reason });
      return;
    }
    dispatch(aptActions.cancelApt({ aptId: aptSignal.detailIdSignal.value, reason, pinCode }));
  };

  const handleConfirmScope = (applyScope: 'SINGLE' | 'SERIES') => {
    const pending = pendingScope;
    setPendingScope(null);
    if (!pending || !aptSignal.detailIdSignal.value) return;

    if (pending.action === 'delete') {
      dispatch(aptActions.deleteApt({
        aptId: aptSignal.detailIdSignal.value,
        pinCode: pending.pinCode,
        applyScope,
      }));
      return;
    }

    dispatch(aptActions.cancelApt({
      aptId: aptSignal.detailIdSignal.value,
      reason: pending.reason || '',
      pinCode: pending.pinCode,
      applyScope,
    }));
  };


  return (
    <>
      <Modal
        title={aptSignal.detailIdSignal.value ? 'DETAIL APPOINTMENT' : 'BOOK APPOINTMENT'}
        open={aptSignal.openSignal.value}
        okText={isValid ? 'BOOK APPOINTMENT' : 'NEXT'}
        onOk={isValid ? handleDone : aptSignal.next}
        disabledOk={aptSignal.getDisabled()}
        visibleOk={!isValid}
        aptFooter={isValid}
        updateAptFooter={isValid && !!aptSignal.detailIdSignal.value}
        onCancel={handleClose}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
        onRepeatApt={handleRepeat}
        onCancelApt={onCancelApt}
        onConfirmApt={onConfirmApt}
        onCheckInApt={onCheckInApt}
        onRevertCheckIn={allowUndoCheckIn ? onRevertCheckIn : undefined}
        onGroup={isValid ? onBookingGroup : undefined}
        checkInDone={aptSignal.detailStatusSignal.value === AppointmentStatus.CHECK_IN}
        confirmed={styleA || aptSignal.detailStatusSignal.value === AppointmentStatus.CONFIRMED}
        width='90vw'
        className='modal-max-height-unset'
        loading={loadingSignal.value}
        style={{ zoom: 0.9 }}
        visibleClose
      >
        <TabsStyled
          defaultActiveKey="1"
          items={items}
          activeKey={activeKey}
          className='cs-ant-tabs'
          tabBarStyle={aptSignal.detailStatusSignal.value === AppointmentStatus.CHECK_IN ? { display: 'none' } : undefined}
          onChange={aptSignal.setActiveKey}
          // @ts-ignore
          indicatorSize={(origin: number) => origin - 16}
          centered
        />
      </Modal>
      <ButtonConfirm
        open={revertPinCode !== null}
        onClose={() => setRevertPinCode(null)}
        onYes={handleRevertCheckIn}
        modalTitle={UNDO_CHECK_IN_CONFIRM.title}
        okLabel={UNDO_CHECK_IN_CONFIRM.okLabel}
        message={UNDO_CHECK_IN_CONFIRM.message}
      />
      <ModalReason visible={!!visibleReason} onClose={() => setVisibleReason('')} onConfirm={handleCancelApt} />
      <ModalConfirmScope
        visible={!!pendingScope}
        action={pendingScope?.action || 'cancel'}
        onClose={() => setPendingScope(null)}
        onConfirm={handleConfirmScope}
      />
      <ModalNumberPad ref={modalNumberPadRef} />
      <ModalConfirmGroup
        onOk={onGroupDone} ref={modalConfirmGroupRef}
        onCancel={onModalConfirmGroupCancel}
      />
    </>
  );
};

export default memo(NewAppointment);

const TabsStyled = styled(Tabs)`
  padding-top: 1rem;
  flex-grow: 1;
  &.cs-ant-tabs.ant-tabs .ant-tabs-ink-bar {
    background: #232F3E;
  }
`;
